import { zodResolver } from '@hookform/resolvers/zod';
import { Search, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from '@/lib/notify';

import { cn } from '@/lib/utils';

import { useCreateRole } from '../api/useCreateRole';
import { useUpdateRole } from '../api/useUpdateRole';
import type { PermissionDto, RoleDto } from '../schemas/roleSchemas';
import { type RoleFormInput, roleFormSchema } from '../schemas/roleSchemas';

// Maps the first segment of a permission code to a human-readable group label.
const PERMISSION_GROUP_LABELS: Record<string, string> = {
  Workflow: 'Workflow Management',
  Session: 'Session Management',
  Questionnaire: 'Questionnaire Management',
  Verification: 'Verification',
  User: 'User Management',
  Role: 'Role Management',
  Tenant: 'Organization Management',
};

interface GroupCheckboxProps {
  checked: boolean;
  indeterminate: boolean;
  disabled: boolean;
  onChange: () => void;
}

function GroupCheckbox({ checked, indeterminate, disabled, onChange }: GroupCheckboxProps) {
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);

  return (
    <label
      className={cn(
        'flex items-center cursor-pointer relative sc-checkbox',
        disabled && 'opacity-50 cursor-not-allowed',
      )}
    >
      <input
        ref={ref}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        className="peer w-5! h-5! rounded-[3px]! border-black-logo! checked:border-primary! indeterminate:border-primary!"
      />
      {/* Full checkmark when all selected */}
      {checked && <span className="sc-check-icon opacity-100 w-4! h-4!" />}
      {/* Dash when partially selected */}
      {indeterminate && (
        <span className="absolute start-0 top-0 w-5 h-5 flex items-center justify-center pointer-events-none">
          <span className="w-2.5 h-[2px] bg-primary rounded-full" />
        </span>
      )}
    </label>
  );
}

interface RoleFormModalProps {
  open: boolean;
  onClose: () => void;
  role?: RoleDto | null;
  permissions: PermissionDto[];
}

export function RoleFormModal({ open, onClose, role, permissions }: RoleFormModalProps) {
  const isEdit = !!role;
  const isSystemRole = isEdit && !!role?.isSystem;
  const createRole = useCreateRole();
  const updateRole = useUpdateRole();
  const isPending = createRole.isPending || updateRole.isPending;

  const [search, setSearch] = useState('');

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<RoleFormInput>({
    resolver: zodResolver(roleFormSchema),
    defaultValues: { name: '', description: '', permissionCodes: [] },
  });

  useEffect(() => {
    if (open) {
      reset({
        name: role?.name ?? '',
        description: role?.description ?? '',
        permissionCodes: role?.permissionCodes ?? [],
      });
      setSearch('');
    }
  }, [open, role, reset]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  const selectedCodes = watch('permissionCodes');

  // Group all permissions by category (first segment of the code).
  const grouped = useMemo(() => {
    const map = new Map<string, PermissionDto[]>();
    for (const p of permissions) {
      const key = p.code.split('.')[0] ?? p.code;
      if (!map.has(key)) map.set(key, []);
      map.get(key)?.push(p);
    }
    return map;
  }, [permissions]);

  // Narrow groups+perms by the search query. Empty query = show all.
  const filteredGrouped = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return grouped;
    const result = new Map<string, PermissionDto[]>();
    for (const [key, perms] of grouped.entries()) {
      const matching = perms.filter(
        (p) => p.description.toLowerCase().includes(q) || p.code.toLowerCase().includes(q),
      );
      if (matching.length > 0) result.set(key, matching);
    }
    return result;
  }, [grouped, search]);

  function togglePermission(code: string) {
    const current = selectedCodes ?? [];
    setValue(
      'permissionCodes',
      current.includes(code) ? current.filter((c) => c !== code) : [...current, code],
      { shouldValidate: true },
    );
  }

  // Bulk-toggles visible permissions in a group. When search is active this
  // only affects the filtered subset so hidden selections are preserved.
  function toggleGroup(key: string) {
    const perms = filteredGrouped.get(key) ?? [];
    const current = selectedCodes ?? [];
    const groupCodes = perms.map((p) => p.code);
    const allChecked = groupCodes.every((c) => current.includes(c));
    setValue(
      'permissionCodes',
      allChecked
        ? current.filter((c) => !groupCodes.includes(c))
        : [...new Set([...current, ...groupCodes])],
      { shouldValidate: true },
    );
  }

  function onSubmit(data: RoleFormInput) {
    if (isEdit && role) {
      updateRole.mutate(
        { roleId: role.id, ...data },
        {
          onSuccess: () => {
            toast.success('Role updated');
            onClose();
          },
          onError: () => toast.error('Failed to update role'),
        },
      );
    } else {
      createRole.mutate(data, {
        onSuccess: () => {
          toast.success('Role created');
          onClose();
        },
        onError: () => toast.error('Failed to create role'),
      });
    }
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/45" onClick={onClose} aria-hidden="true" />
      <div className="flex justify-center p-5 lg:py-16 h-full w-full">
        <div className="relative w-full max-w-[701px] rounded-3xl p-5 lg:p-7.5 flex flex-col gap-5 lg:gap-6 bg-white border border-placeholder shadow-[0px_0px_18.6px_0px_#0000001A] max-lg:h-fit max-lg:max-h-full overflow-hidden animate-modal text-black-logo tracking-tight">
          {/* Header */}
          <div className="flex items-center justify-between gap-4">
            <h2 className="text-base sm:text-lg lg:text-xl leading-6 font-semibold text-black tracking-normal">
              {isEdit ? 'Edit Role' : 'Create New Role'}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="w-6 h-6 rounded-full bg-[#E5EDFF] text-primary flex items-center justify-center shrink-0 hover:bg-[#d7e3ff] transition"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Scrollable body */}
          <form
            id="role-form"
            onSubmit={handleSubmit(onSubmit)}
            className="overflow-y-auto flex-1 min-h-0 lg:mt-2 space-y-4 lg:space-y-6 px-px custom-scrollbar"
          >
            <div className="space-y-2.5 lg:space-y-3.5">
              <div className="flex flex-col gap-1">
                <label htmlFor="role-name" className="sc-input-label" data-required>
                  Role Name
                </label>
                <input
                  id="role-name"
                  type="text"
                  className="sc-input py-[11.5px]"
                  placeholder="e.g. HR Manager"
                  readOnly={isSystemRole}
                  {...register('name')}
                />
                {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
              </div>
              <div className="flex flex-col gap-1">
                <label htmlFor="role-desc" className="sc-input-label">
                  Description
                </label>
                <textarea
                  id="role-desc"
                  className="sc-textarea min-h-22.5"
                  placeholder="Describe what this role can do…"
                  readOnly={isSystemRole}
                  {...register('description')}
                />
                {errors.description && (
                  <p className="text-xs text-destructive">{errors.description.message}</p>
                )}
              </div>
            </div>

            <div className="flex flex-col gap-2.5">
              <h5 className="text-base lg:text-lg leading-[150%] font-semibold">
                Access Permissions
              </h5>
              <div className="border border-[#E4E4E4] bg-[#F9FCFF] rounded-xl p-2.5 space-y-3">
                {/* Search — always visible, outside the scroll area */}
                <div className="relative">
                  <Search className="w-3.5 h-3.5 absolute top-1/2 start-3 -translate-y-1/2 text-gray66 pointer-events-none" />
                  <input
                    type="search"
                    className="sc-input input-small ps-8 pe-8 w-full"
                    placeholder="Search permissions…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  {search && (
                    <button
                      type="button"
                      onClick={() => setSearch('')}
                      className="absolute top-1/2 end-3 -translate-y-1/2 text-gray66 hover:text-black-logo"
                      aria-label="Clear search"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>

                {/* Permission groups — scrollable */}
                <div className="max-h-64 overflow-y-auto custom-scrollbar space-y-4 lg:space-y-5 pb-0.5">
                  {filteredGrouped.size === 0 && permissions.length > 0 && (
                    <p className="text-sm text-gray66 text-center py-2">
                      No permissions match &ldquo;{search}&rdquo;.
                    </p>
                  )}
                  {permissions.length === 0 && (
                    <p className="text-sm text-gray66">No permissions available.</p>
                  )}
                  {[...filteredGrouped.entries()].map(([key, perms]) => {
                    const groupCodes = perms.map((p) => p.code);
                    const checkedCount = groupCodes.filter((c) =>
                      (selectedCodes ?? []).includes(c),
                    ).length;
                    const allChecked = checkedCount === groupCodes.length && groupCodes.length > 0;
                    const someChecked = checkedCount > 0 && checkedCount < groupCodes.length;

                    return (
                      <div key={key}>
                        {/* Group header with bulk-select checkbox */}
                        <div className="flex items-center gap-2 mb-3">
                          <GroupCheckbox
                            checked={allChecked}
                            indeterminate={someChecked}
                            disabled={isSystemRole}
                            onChange={() => toggleGroup(key)}
                          />
                          <h5 className="text-base leading-[150%] font-semibold text-primary">
                            {PERMISSION_GROUP_LABELS[key] ?? key}
                          </h5>
                        </div>
                        {/* Individual permissions, indented to align under the label */}
                        <ul className="space-y-3 lg:space-y-5 ps-7">
                          {perms.map((p) => {
                            const checked = (selectedCodes ?? []).includes(p.code);
                            return (
                              <li key={p.code} className="flex items-start gap-2">
                                <label
                                  className={cn(
                                    'flex items-center cursor-pointer relative sc-checkbox translate-y-0.5',
                                    isSystemRole && 'opacity-50 cursor-not-allowed',
                                  )}
                                >
                                  <input
                                    type="checkbox"
                                    className="peer w-5! h-5! rounded-[3px]! border-black-logo! checked:border-primary!"
                                    checked={checked}
                                    disabled={isSystemRole}
                                    onChange={() => togglePermission(p.code)}
                                  />
                                  <span className="sc-check-icon opacity-0 peer-checked:opacity-100 w-4! h-4!" />
                                </label>
                                <div className="space-y-0.5">
                                  <h6 className="text-sm leading-[17px] font-medium">
                                    {p.description}
                                  </h6>
                                  <p className="text-xs leading-3.5 text-gray66">{p.code}</p>
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    );
                  })}
                </div>
              </div>
              <p className="text-sm leading-[150%] font-medium text-grayA5 mt-[3px]">
                Configure specific actions this role can perform
              </p>
            </div>
          </form>

          {/* Footer */}
          <div className="flex sm:justify-end items-center gap-3.5">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-outlined btn-small text-primary! hover:bg-primary/10!"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="role-form"
              disabled={isPending || isSystemRole}
              className="btn btn-primary btn-small px-2.5!"
            >
              {isPending ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Role'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
